import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    required,
    email,
    useRecordContext,
} from 'react-admin';
import { Alert, Box, Card, CardContent, Chip, Divider, Typography } from '@mui/material';
import { FormToolbar } from '../../common/FormToolbar';
import { ProvisionButton } from './ProvisionButton';

/**
 * Editing a school, plus the panel that says what state its database is in.
 *
 * The panel is read-only on purpose. Every value in it -- database name,
 * schema version, status -- is written by the provisioner or the migration
 * runner, and each one is a fact about the world rather than a preference. A
 * text box that let someone type a different db_name would not move the
 * database; it would only make Master lie about where the school lives, and
 * the school would stop resolving with no clue as to why.
 *
 * The one action here is Provision, which runs the real thing.
 */

/** What the two status columns mean, together, in one sentence. */
const ProvisionPanel = () => {
    const record = useRecordContext();
    if (!record) return null;

    const rows: Array<[string, any]> = [
        ['Database', record.db_name || '— not created yet'],
        ['Host', record.db_name ? `${record.db_host}:${record.db_port}` : '—'],
        ['Schema version', record.schema_version === null ? '—' : `v${record.schema_version}`],
        ['Last migrated', record.last_migrated_at || '—'],
        ['School status', record.status],
        ['Database status', record.db_status || '—'],
    ];

    return (
        <Card variant="outlined" sx={{ width: '100%', mb: 3 }}>
            <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                    Database
                </Typography>

                {!record.provisioned && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This school has no database. Nobody can sign in to it and no request can
                        reach it. Press <b>Provision database</b> to build one.
                    </Alert>
                )}

                {record.provisioned && !record.routable && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        The database exists but the school is not serving traffic &mdash; school
                        status is <b>{record.status}</b> and database status is{' '}
                        <b>{record.db_status}</b>. Routing needs both to be <b>active</b>.
                    </Alert>
                )}

                {record.routable && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Serving from <b>{record.db_name}</b> at schema v{record.schema_version}.
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content 1fr',
                        columnGap: 3,
                        rowGap: 0.75,
                        mb: 2,
                    }}
                >
                    {rows.map(([label, value]) => (
                        <React.Fragment key={label}>
                            <Typography variant="body2" color="text.secondary">
                                {label}
                            </Typography>
                            <Typography variant="body2">{value}</Typography>
                        </React.Fragment>
                    ))}
                </Box>

                {record.members && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                            Members
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <Chip size="small" label={`${record.members.administrators} admin`} />
                            <Chip size="small" label={`${record.members.teachers} teachers`} />
                            <Chip size="small" label={`${record.members.students} students`} />
                        </Box>
                    </>
                )}

                <ProvisionButton record={record} />
            </CardContent>
        </Card>
    );
};

/** Shown rather than editable -- see index_put()'s refusal on the API side. */
const ImmutableCode = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <TextInput
            source="code"
            label="School code"
            disabled
            helperText="Immutable. It is in the branding subdomain and in every backup manifest this school has."
            sx={{ flex: 1 }}
        />
    );
};

export const SchoolEdit = props => (
    <Edit {...props} title="School" mutationMode="pessimistic">
        <SimpleForm toolbar={<FormToolbar {...props} />}>
            <ProvisionPanel />

            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <ImmutableCode />
                <TextInput
                    source="name"
                    label="School name"
                    validate={[required()]}
                    sx={{ flex: 2 }}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <TextInput source="board" label="Board" sx={{ flex: 1 }} />
                <TextInput source="city" label="City" sx={{ flex: 1 }} />
                <TextInput source="state" label="State" sx={{ flex: 1 }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <TextInput source="country" label="Country" sx={{ flex: 1 }} />
                <TextInput source="timezone" label="Timezone" sx={{ flex: 1 }} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <TextInput source="contact_name" label="Contact person" sx={{ flex: 1 }} />
                <TextInput
                    source="contact_email"
                    label="Contact email"
                    validate={[email()]}
                    sx={{ flex: 1 }}
                />
                <TextInput source="contact_phone" label="Contact phone" sx={{ flex: 1 }} />
            </Box>
        </SimpleForm>
    </Edit>
);
